import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useCompanyAdminAuth } from "../contexts/CompanyAdminAuthContext";
import { companyAdminBillingAPI } from "../lib/companyAdminApi";
import { ADMIN_QUERY } from "../lib/adminQuery";
import {
  COMPANY_CURRENCIES,
  convertBetweenRates,
  formatCompanyMoney,
  formatConvertedMoney,
  fromUsd,
  normalizeCurrency,
} from "../lib/money";

export function useUsdRates() {
  const { data } = useQuery({
    queryKey: ["admin", "exchange-rates"],
    queryFn: async () => {
      const res = await companyAdminBillingAPI.exchangeRates();
      return res.data || {};
    },
    ...ADMIN_QUERY,
    staleTime: 30 * 60 * 1000,
  });
  return {
    rates: data?.rates || { USD: 1 },
    currencies: data?.currencies?.length ? data.currencies : COMPANY_CURRENCIES,
    currency: data?.currency,
  };
}

export function useCompanyMoney() {
  const { company, companyConfig } = useCompanyAdminAuth();
  const { rates, currencies } = useUsdRates();
  const currency = normalizeCurrency(
    company?.currency || companyConfig?.currency,
  );

  return useMemo(
    () => ({
      currency,
      rates,
      currencies,
      money: (amount, from = currency) =>
        formatConvertedMoney(amount, from, currency, rates),
      fromUsd: (amount) => formatConvertedMoney(amount, "USD", currency, rates),
      toDisplay: (amount, from = currency) =>
        convertBetweenRates(amount, from, currency, rates),
      fromUsdNumber: (amount) => fromUsd(amount, currency, rates),
      format: (amount) => formatCompanyMoney(amount, currency),
    }),
    [currency, rates, currencies],
  );
}

/** Drop cached converted amounts so UI follows the new company currency without a full reload. */
export function invalidateCompanyMoneyQueries(queryClient) {
  if (!queryClient) return Promise.resolve();
  return Promise.all(
    [
      ["admin", "exchange-rates"],
      ["admin", "billing"],
      ["admin", "addons"],
      ["admin", "appointments"],
      ["admin", "customers"],
      ["admin", "payment-history"],
      ["admin", "chat-appointments"],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}
